import DOMPurify from 'dompurify';
import { isAllowedCommentImageSrc, isGiphyMediaSrc } from '../../components/RichText';

/**
 * Turning a stored comment into HTML safe to put on the page.
 *
 * Posted comments are rendered STATICALLY — no editor is instantiated for a
 * thread. A project discussion can run to a hundred posts, and a hundred
 * ProseMirror instances is a hundred document schemas, plugin stacks and
 * DOM observers for text nobody is editing.
 *
 * ── Why DOMPurify does not simply replace the existing sanitizer ───────────
 *
 * sanitizeRichText (components/RichText.tsx) is the WRITE-side door: it
 * normalizes what arrives from a Google Docs or Slack paste into the app's own
 * subset. It also enforces something DOMPurify does not do out of the box and
 * which is easy to lose in a migration: an ALLOWLIST OF IMAGE HOSTS.
 *
 * That allowlist is a privacy control, not a formatting one. Comments are
 * user-written HTML, `<img>` is the one element that makes a reader's browser
 * fetch from somewhere else, and a foreign src is a tracking pixel that
 * reports who opened a supplier thread and when. DOMPurify's defaults happily
 * keep any https image.
 *
 * So the hook below re-applies it. Swapping sanitizers without it would have
 * been a silent regression of a real control — everything would still look
 * right, and the leak would be invisible.
 */

/**
 * The tags a posted comment may use.
 *
 * `span` is here only because a mention is one; every other span is stripped
 * of its attributes by the allowlist below, so it renders as plain text.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'del',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'span',
  'mark',
  'blockquote',
];

/**
 * The attributes that survive.
 *
 * No `style`, no `class` beyond the ones added below, and no `on*` — DOMPurify
 * drops event handlers by itself, but the narrow list is what stops a stored
 * comment carrying layout that fights the page.
 */
const ALLOWED_ATTR = [
  'href',
  'src',
  'alt',
  'title',
  'data-type',
  'data-id',
  'data-label',
  'data-href',
  'class',
];

let hooksInstalled = false;

/**
 * Install the hooks once per process.
 *
 * DOMPurify hooks are global to the instance, so adding them on every call
 * would stack duplicates and run the same check dozens of times per thread.
 */
function installHooks(): void {
  if (hooksInstalled) {
    return;
  }
  hooksInstalled = true;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'IMG') {
      // THE privacy control. Re-applied here because DOMPurify's own defaults
      // permit any https image, and a foreign one reports who opened the
      // thread and when. Our Drive hosts, plus GIPHY's media hosts for the
      // GIF button (Drop 3b); https only, see isAllowedCommentImageSrc.
      const src = node.getAttribute('src') ?? '';
      if (!isAllowedCommentImageSrc(src)) {
        node.remove();
        return;
      }
      // A GIF is fetched from GIPHY by every reader's browser. The Hub sets
      // no Referrer-Policy of its own, so the browser default applies and
      // each fetch tells GIPHY the Hub's address. The picker grid and the
      // search call already send no referrer; this makes the posted comment
      // match. referrerpolicy is not in ALLOWED_ATTR, so any value a stored
      // body carried is already gone by the time this runs, and the one set
      // here is the only one that can be present.
      // Drive images are left as they were (Claude's 3b-1 answer 7 covers
      // GIPHY only; Drive is our own Google Workspace).
      if (isGiphyMediaSrc(src)) {
        node.setAttribute('referrerpolicy', 'no-referrer');
      }
    }

    if (node.tagName === 'A') {
      const href = node.getAttribute('href') ?? '';
      // javascript: and data: URLs in a link are script execution one click
      // away. DOMPurify blocks the obvious forms; this refuses everything
      // that is not plainly a web address or a mail link.
      if (!/^(https?:|mailto:|\/)/i.test(href)) {
        node.removeAttribute('href');
      }
      // A comment can link anywhere. Without noopener the opened page can
      // reach back through window.opener.
      if (node.getAttribute('target') === '_blank' || href.startsWith('http')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }

    // The @person highlight. renderCommentHtml wraps each known person's name
    // in <mark class="comment-mention"> AFTER sanitizing, on text nodes only.
    // That marking is what makes a notification visible in the thread, so the
    // tag survives — but only with that one class, so a stored comment cannot
    // dress arbitrary text up as somebody being mentioned.
    if (node.tagName === 'MARK') {
      if (node.getAttribute('class') === 'comment-mention') {
        node.setAttribute('class', 'comment-mention');
      } else {
        node.removeAttribute('class');
      }
    }

    // Only the mention span keeps its class; anything else claiming to be one
    // is a stored comment trying to borrow the styling.
    if (node.tagName === 'SPAN') {
      const isMention = node.getAttribute('data-type') === 'mention';
      if (!isMention) {
        node.removeAttribute('class');
        node.removeAttribute('data-id');
        node.removeAttribute('data-label');
        node.removeAttribute('data-href');
      } else {
        node.setAttribute('class', 'comment-mention-pill');
        // data-href becomes a navigation target on the pill, which makes it
        // exactly as dangerous as an <a href> and nothing else was checking
        // it. A mention points at a record INSIDE the hub, so anything that
        // is not an in-app path is refused outright rather than sanitized
        // towards — there is no legitimate off-site mention.
        const target = node.getAttribute('data-href') ?? '';
        if (!target.startsWith('/')) {
          node.removeAttribute('data-href');
        }
      }
    }
  });
}

/** Sanitize a stored comment for rendering. Never returns unsafe HTML. */
export function sanitizeCommentHtml(html: string): string {
  installHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Keep the text of a stripped tag rather than deleting it with the tag —
    // a comment should never lose words to sanitizing.
    KEEP_CONTENT: true,
  });
}

/** A uuid, and nothing else, is a mentioned_user_id. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The people @named in a comment, read back out of its HTML.
 *
 * This is what decides which comment_mentions rows get written, and therefore
 * who is notified. Read from the SANITIZED html rather than tracked alongside
 * the editor for the same reason mentionsIn is: the sanitizer is allowed to
 * drop things, and a list built before it ran would notify people about a pill
 * that is no longer in the comment.
 *
 * The shape is checked because the value goes into a uuid column. A malformed
 * id would be refused by Postgres, and the whole insert with it — so one
 * mangled pill would silently cost every other person in the comment their
 * notification.
 *
 * This is NOT an access control. Nothing here decides who may be mentioned:
 * comment_mentions_guard() refuses anybody who is not an active TAP account,
 * and mentions grant no visibility at all.
 */
export interface PersonMention {
  id: string;
  label: string;
}

export function personMentionsIn(html: string): PersonMention[] {
  const holder = document.createElement('div');
  holder.innerHTML = sanitizeCommentHtml(html);
  const found = new Map<string, PersonMention>();
  for (const node of holder.querySelectorAll('mark.comment-mention[data-id]')) {
    const id = node.getAttribute('data-id') ?? '';
    if (!UUID.test(id)) {
      continue;
    }
    // The unique constraint is (comment_id, mentioned_user_id), so naming
    // somebody twice in one comment would fail the whole insert.
    if (!found.has(id)) {
      const label = node.getAttribute('data-label') ?? (node.textContent ?? '').replace(/^@/, '');
      found.set(id, { id, label });
    }
  }
  return [...found.values()];
}

export function personMentionIds(html: string): string[] {
  return personMentionsIn(html).map((mention) => mention.id);
}

export interface MentionPill {
  id: string;
  label: string;
  href: string;
}

/**
 * The mentions in a rendered comment, so the card can make them clickable.
 *
 * Read back out of the sanitized HTML rather than tracked alongside it: the
 * sanitizer is allowed to drop things, and a list built before it ran would
 * promise pills for spans that no longer exist.
 */
export function mentionsIn(html: string): MentionPill[] {
  const holder = document.createElement('div');
  holder.innerHTML = sanitizeCommentHtml(html);
  return Array.from(holder.querySelectorAll('span[data-type="mention"]')).map((node) => ({
    id: node.getAttribute('data-id') ?? '',
    label: node.getAttribute('data-label') ?? node.textContent ?? '',
    href: node.getAttribute('data-href') ?? '',
  }));
}
